import { useRouter } from "next/router";
import React, { useEffect, useReducer, useCallback } from "react";
import OTPInput from "react-otp-input";
import toast from "react-hot-toast";
import axios from "axios";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import Link from "next/link";
import Head from "next/head";

// Import your components (assuming these are available)
import ImageWrapper from "@/components/ImageComponent/ImageWrapper";
import Spinner from "@/components/SpinnerComponent/Spinners";
import GoogleCaptchaWrapper from "@/components/AuthComponents/ReCaptchaComponent";
import { getCookieItem, setCookieItem } from "@/utils/utils";
import { GETREQUESTAPI_AUTHENTICATED, POSTREQUESTAPI } from "../api/apiUtils";
import {
    AUTH_VERIFY_OTP,
    BACKEND_API_ROUTE,
    UPDATE_USER_API_URL,
    GET_USER_DETAILS_API,
    SEND_OTP_RELAY_ROUTE,
} from "@/commons/api_routes";
import { Phone } from "lucide-react";

// Constants
const OTP_LENGTH = 6;
const PHONE_LENGTH = 10;
const RESEND_COUNTDOWN = 60;
const RECAPTCHA_THRESHOLD = 0.5;

// Initial State
const initialState = {
    isPhoneInputVisible: true,
    otpSent: false,
    error: "",
    phoneValue: "",
    showOtpButton: false,
    otp: "",
    resendDisabled: false,
    resendCountdown: RESEND_COUNTDOWN,
    isValidNumber: false,
    isFocused: false,
    formData: {
        phone: "",
        userName: "",
        gender: "",
        dob: "",
        email: "",
    },
    phoneVerified: false,
    showAdditionalInputs: false,
    loading: false,
};

// Reducer
const reducer = (state, action) => {
    switch (action.type) {
        case "SET_PHONE_INPUT_VISIBLE":
            return { ...state, isPhoneInputVisible: action.payload };
        case "SET_OTP_SENT":
            return { ...state, otpSent: action.payload };
        case "SET_ERROR":
            return { ...state, error: action.payload };
        case "SET_PHONE_VALUE":
            return { ...state, phoneValue: action.payload };
        case "SET_SHOW_OTP_BUTTON":
            return { ...state, showOtpButton: action.payload };
        case "SET_OTP":
            return { ...state, otp: action.payload };
        case "SET_RESEND_DISABLED":
            return { ...state, resendDisabled: action.payload };
        case "SET_RESEND_COUNTDOWN":
            return { ...state, resendCountdown: action.payload };
        case "SET_IS_VALID_NUMBER":
            return { ...state, isValidNumber: action.payload };
        case "SET_FOCUS":
            return { ...state, isFocused: action.payload };
        case "SET_FORM_DATA":
            return {
                ...state,
                formData: { ...state.formData, ...action.payload }
            };
        case "SET_PHONE_VERIFIED":
            return { ...state, phoneVerified: action.payload };
        case "SET_SHOW_ADDITIONAL_INPUTS":
            return { ...state, showAdditionalInputs: action.payload };
        case "SET_IS_LOADING":
            return { ...state, loading: action.payload };
        case "RESET_PHONE_STATE":
            return {
                ...state,
                isPhoneInputVisible: true,
                showOtpButton: true,
                otp: "",
                error: "",
                otpSent: false,
                resendDisabled: false,
                resendCountdown: RESEND_COUNTDOWN,
            };
        default:
            return state;
    }
};

// Validation Functions
const validatePhoneNumber = (phone) => {
    const numericValue = phone.replace(/\D/g, "");

    if (!numericValue) {
        return { isValid: false, error: "Mobile number is required." };
    }

    if (numericValue.length !== PHONE_LENGTH) {
        return { isValid: false, error: "Mobile number must be 10 digits." };
    }

    if (!/^[6-9]\d{9}$/.test(numericValue)) {
        return {
            isValid: false,
            error: "Please enter a valid Indian mobile number starting with 6-9."
        };
    }

    return { isValid: true, error: "" };
};

const validateFormData = (formData) => {
    const errors = [];

    if (!formData.userName?.trim()) {
        errors.push("Name is required");
    } else if (formData.userName.trim().length < 2) {
        errors.push("Name must be at least 2 characters");
    }

    if (!formData.gender) {
        errors.push("Gender is required");
    }

    return errors;
};

// Main Component
export default function SignUpLogin() {
    return (
        <GoogleCaptchaWrapper>
            <HomeInside />
        </GoogleCaptchaWrapper>
    );
}

const HomeInside = () => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const router = useRouter();
    const { executeRecaptcha } = useGoogleReCaptcha();

    // Format phone number for display
    const formatPhoneNumber = useCallback((phone) => {
        if (phone.length !== PHONE_LENGTH) return phone;
        return `+91-${phone.substring(0, 3)}-${phone.substring(3, 6)}-${phone.substring(6)}`;
    }, []);

    // Handle Recaptcha and OTP Send
    const handleSubmitForm = useCallback((e) => {
        e.preventDefault();

        if (!executeRecaptcha) {
            toast.error("Recaptcha not available. Please refresh the page.");
            return;
        }

        const validation = validatePhoneNumber(state.phoneValue);
        if (!validation.isValid) {
            dispatch({ type: "SET_ERROR", payload: validation.error });
            return;
        }

        dispatch({ type: "SET_IS_LOADING", payload: true });

        executeRecaptcha("enquiryFormSubmit")
            .then((gReCaptchaToken) => submitEnquiryForm(gReCaptchaToken))
            .catch((error) => {
                toast.error("Recaptcha verification failed");
                dispatch({ type: "SET_IS_LOADING", payload: false });
            });
    }, [executeRecaptcha, state.phoneValue]);

    const submitEnquiryForm = async (gReCaptchaToken, isManualVerification = false) => {
        try {
            const response = await axios.post("/api/registerFormSubmit", {
                mobileNumber: state.phoneValue,
                gRecaptchaToken: gReCaptchaToken,
            });

            if (!isManualVerification && response?.data?.score <= RECAPTCHA_THRESHOLD) {
                toast.error("Bot detected. Please verify manually.");
                const manualToken = await executeRecaptcha("manualVerification");
                await submitEnquiryForm(manualToken, true);
            } else if (response?.data?.success === true) {
                await handleSendOTPClick();
            } else {
                toast.error(`Verification failed. Score: ${response?.data?.score}`);
            }
        } catch (error) {
            toast.error("Failed to verify. Please try again.");
        } finally {
            dispatch({ type: "SET_IS_LOADING", payload: false });
        }
    };

    // Send OTP
    const handleSendOTPClick = async () => {
        dispatch({ type: "SET_IS_LOADING", payload: true });
        dispatch({ type: "SET_ERROR", payload: "" });

        try {
            const sendOTPUrl = SEND_OTP_RELAY_ROUTE;
            const response = await POSTREQUESTAPI(sendOTPUrl, {
                phone: `+91-${state.phoneValue}`,
            });

            if (response?.status === 201) {
                const data = await response.data;
                if (data.statusCode === 200) {
                    toast.success(data.message || "OTP sent successfully");
                    dispatch({ type: "SET_OTP", payload: "" });
                    dispatch({ type: "SET_PHONE_INPUT_VISIBLE", payload: false });
                    dispatch({ type: "SET_OTP_SENT", payload: true });
                    dispatch({ type: "SET_RESEND_DISABLED", payload: true });
                    dispatch({ type: "SET_RESEND_COUNTDOWN", payload: RESEND_COUNTDOWN });
                } else {
                    throw new Error(data.message || "Failed to send OTP");
                }
            } else {
                throw new Error("Failed to send OTP");
            }
        } catch (error) {
            toast.error(error.message || "Error sending OTP");
            dispatch({ type: "SET_ERROR", payload: error.message });
        } finally {
            dispatch({ type: "SET_IS_LOADING", payload: false });
        }
    };

    // Verify OTP
    const handleVerifyOTPClick = async () => {
        if (state.otp.length !== OTP_LENGTH) {
            dispatch({ type: "SET_ERROR", payload: "Please enter a valid 6-digit OTP" });
            return;
        }

        dispatch({ type: "SET_IS_LOADING", payload: true });
        dispatch({ type: "SET_ERROR", payload: "" });

        try {
            const userVerifyUrl = BACKEND_API_ROUTE + AUTH_VERIFY_OTP;
            const response = await POSTREQUESTAPI(userVerifyUrl, {
                otp: state.otp,
                phone: `+91-${state.phoneValue}`,
            });

            if (response?.status === 201) {
                const data = await response.data;
                if (data.statusCode === 200) {
                    toast.success(data.message || "OTP verified successfully");
                    setCookieItem("accessToken", data.body.accessToken);
                    await handleUpdateUserDetails();
                } else {
                    throw new Error(data.error || "Invalid OTP");
                }
            } else {
                throw new Error("OTP verification failed");
            }
        } catch (error) {
            toast.error(error.message || "Invalid OTP. Please try again.");
            dispatch({ type: "SET_ERROR", payload: error.message });
        } finally {
            dispatch({ type: "SET_IS_LOADING", payload: false });
        }
    };

    // Get User Details
    const handleUpdateUserDetails = async () => {
        const redirecturl = router.query.redirect;

        try {
            const userDetailUrl = BACKEND_API_ROUTE + GET_USER_DETAILS_API;
            const accessToken = getCookieItem("accessToken");
            const response = await GETREQUESTAPI_AUTHENTICATED(userDetailUrl, accessToken);

            if (response?.status === 200) {
                const userDetails = await response.data;
                if (userDetails.statusCode === 200) {
                    const userBody = userDetails.body;

                    const needsDetails =
                        (!userBody.hasOwnProperty("isEmailVerified") || !userBody.isEmailVerified) &&
                        (!userBody.hasOwnProperty("userName") || !userBody.userName);

                    if (needsDetails) {
                        toast.success("Please complete your profile");
                        dispatch({ type: "SET_SHOW_ADDITIONAL_INPUTS", payload: true });
                    } else {
                        toast.success("Login successful");
                        const path = redirecturl ? decodeURIComponent(redirecturl) : "/";
                        router.push(path);
                    }
                } else {
                    throw new Error("Failed to fetch user details");
                }
            } else {
                throw new Error("Failed to fetch user details");
            }
        } catch (error) {
            toast.error(error.message || "Error fetching user details");
        }

        dispatch({ type: "SET_OTP", payload: "" });
        dispatch({ type: "SET_PHONE_VERIFIED", payload: true });
    };

    // Handle Phone Input Change
    const handlePhoneInputChange = (e) => {
        const inputValue = e.target.value;
        const numericValue = inputValue.replace(/\D/g, "");

        if (numericValue.length > PHONE_LENGTH) return;

        const validation = validatePhoneNumber(numericValue);

        dispatch({ type: "SET_PHONE_VALUE", payload: numericValue });
        dispatch({ type: "SET_SHOW_OTP_BUTTON", payload: validation.isValid });
        dispatch({ type: "SET_IS_VALID_NUMBER", payload: validation.isValid });
        dispatch({ type: "SET_ERROR", payload: validation.error });

        if (validation.isValid) {
            dispatch({
                type: "SET_FORM_DATA",
                payload: { phone: numericValue }
            });
        }
    };

    // Handle OTP Change
    const handleOTPChange = (otpValue) => {
        dispatch({ type: "SET_OTP", payload: otpValue });
        dispatch({ type: "SET_ERROR", payload: "" });
    };

    const handleFocus = () => {
        dispatch({ type: "SET_FOCUS", payload: true });
    };

    const handleBlur = () => {
        dispatch({ type: "SET_FOCUS", payload: false });
        if (state.phoneValue.length > 0 && state.phoneValue.length < PHONE_LENGTH) {
            dispatch({ type: "SET_ERROR", payload: "Please enter a valid 10-digit mobile number" });
        } else {
            dispatch({ type: "SET_ERROR", payload: "" });
        }
    };

    // Handle Phone Change Click
    const handlePhoneChangeClick = () => {
        dispatch({ type: "RESET_PHONE_STATE" });
    };

    // Handle Resend OTP
    const handleResendOTPClick = async () => {
        dispatch({ type: "SET_ERROR", payload: "" });
        dispatch({ type: "SET_OTP", payload: "" });
        await handleSendOTPClick();
    };

    // Handle Finish Button
    const handleFinishButtonClick = async () => {
        const redirecturl = router.query.redirect;
        const errors = validateFormData(state.formData);

        if (errors.length > 0) {
            toast.error(errors[0]);
            return;
        }

        dispatch({ type: "SET_IS_LOADING", payload: true });

        try {
            const accessToken = getCookieItem("accessToken");
            const userUpdateUrl = BACKEND_API_ROUTE + UPDATE_USER_API_URL;

            const updateData = { ...state.formData };
            delete updateData.phone;

            const response = await POSTREQUESTAPI(userUpdateUrl, updateData, accessToken);

            if (response?.status === 201) {
                const data = await response.data;
                if (data.statusCode === 200) {
                    toast.success(data.message || "Profile updated successfully");
                    const path = redirecturl ? decodeURIComponent(redirecturl) : "/";
                    router.push(path);
                } else {
                    throw new Error("Failed to update profile");
                }
            } else {
                throw new Error("Failed to update profile");
            }
        } catch (error) {
            toast.error(error.message || "Error updating profile");
        } finally {
            dispatch({ type: "SET_IS_LOADING", payload: false });
        }
    };

    // Countdown Timer Effect
    useEffect(() => {
        let timer;
        if (state.resendCountdown > 0 && state.resendDisabled) {
            timer = setInterval(() => {
                dispatch({
                    type: "SET_RESEND_COUNTDOWN",
                    payload: state.resendCountdown - 1,
                });
            }, 1000);
        } else if (state.resendCountdown === 0) {
            dispatch({ type: "SET_RESEND_DISABLED", payload: false });
        }
        return () => clearInterval(timer);
    }, [state.resendCountdown, state.resendDisabled]);

    return (
        <>
        <Head>
            <title>Login / Sign Up | BookMyPooja</title>
            <meta name="description" content="Log in or sign up to BookMyPooja with your phone number to book verified pandits and pujas online." />
            <meta name="robots" content="noindex, follow" />
        </Head>
        <div className="relative min-h-screen w-full flex items-end md:items-center justify-center  p-4 md:p-6 pb-32 md:pb-8">
            {/* Background Design */}
            <div className="absolute inset-0 -z-10 overflow-hidden">
                <div className="relative w-full h-full">
                    <ImageWrapper
                        src="/icons/Group6473.svg"
                        alt=""
                        fill
                        className="object-cover"
                    />
                </div>
                <div className="hidden sm:flex absolute inset-0 items-center justify-center -z-10">
                    <div className="relative h-[87px] w-[81px]">
                        <ImageWrapper
                            src="/icons/Group6935.svg"
                            alt=""
                            fill
                            className="object-contain"
                        />
                    </div>
                </div>
            </div>

            {/* Main Container */}
            <div className="flex flex-col w-full max-w-md mx-auto space-y-4">
                {/* Logo */}
                <div className="flex justify-center">
                    <div
                        className="relative w-48 h-24 p-1 cursor-pointer"
                        onClick={() => router.push("/")}
                    >
                        <ImageWrapper
                            src="/icons/book-my-pooja.svg"
                            alt="BookMyPooja Logo"
                            fill
                            className="object-contain"
                        />
                    </div>
                </div>

                {/* Form Card */}
                <div className="bg-white rounded-2xl drop-shadow-xl p-4 md:p-6 space-y-6">
                    <h2 className="text-xl md:text-2xl font-medium text-gray-800 text-center tracking-tight">
                        Login / Sign up
                    </h2>

                    {state.loading ? (
                        <div className="flex justify-center py-8">
                            <Spinner loading={state.loading} />
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Phone Section */}
                            {!state.phoneVerified && (
                                <div className={`${!state.isPhoneInputVisible ? 'border border-gray-300' : ''} rounded-md`}>
                                    {state.isPhoneInputVisible ? (
                                        <div className="p-3 space-y-3">
                                            <label className="block">
                                                <span className="text-xs text-gray-600">
                                                    Phone Number <span className="text-red-500">*</span>
                                                </span>
                                            </label>

                                            <div className={`border rounded-lg transition-all duration-200 ${state.error
                                                    ? 'border-red-500'
                                                    : state.isFocused
                                                        ? 'border-blue-500 ring-2 ring-blue-100'
                                                        : 'border-gray-300'
                                                }`}>
                                                <div className="flex items-center px-3 py-2.5">
                                                    <div className="flex-shrink-0 mr-3">
                                                        <Phone className={`w-4 h-4 ${state.error
                                                                ? 'text-red-500'
                                                                : state.isFocused
                                                                    ? 'text-blue-500'
                                                                    : 'text-gray-400'
                                                            }`} />
                                                    </div>

                                                    <input
                                                        type="tel"
                                                        placeholder="Enter 10-digit mobile number"
                                                        className="flex-1 bg-transparent focus:outline-none text-sm text-gray-900 placeholder-gray-400"
                                                        value={state.phoneValue}
                                                        onChange={handlePhoneInputChange}
                                                        onFocus={handleFocus}
                                                        onBlur={handleBlur}
                                                        maxLength={PHONE_LENGTH}
                                                    />

                                                    {state.phoneValue.length > 0 && (
                                                        <span className="text-xs text-gray-500 ml-2">
                                                            {state.phoneValue.length}/{PHONE_LENGTH}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {state.error && (
                                                <p className="text-xs text-red-500 flex items-center">
                                                    <span className="inline-block w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                                    {state.error}
                                                </p>
                                            )}

                                            {!state.error && state.phoneValue.length === PHONE_LENGTH && (
                                                <p className="text-xs text-green-600 flex items-center">
                                                    <span className="inline-block w-1 h-1 bg-green-600 rounded-full mr-2"></span>
                                                    Valid phone number
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="p-3 space-y-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-gray-700">
                                                    {formatPhoneNumber(state.phoneValue)}
                                                </span>
                                                <button
                                                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                                    onClick={handlePhoneChangeClick}
                                                >
                                                    Change
                                                </button>
                                            </div>

                                            <div className="space-y-2">
                                                <p className="text-xs text-gray-600">
                                                    Enter OTP sent to your mobile number *
                                                </p>
                                                <OTPInput
                                                    inputType="tel"
                                                    value={state.otp}
                                                    onChange={handleOTPChange}
                                                    numInputs={OTP_LENGTH}
                                                    renderSeparator={<span className="w-2" />}
                                                    renderInput={(props) => <input {...props} />}
                                                    inputStyle={{
                                                        border: "1px solid #94A3B8",
                                                        borderRadius: "4px",
                                                        width: "40px",
                                                        height: "40px",
                                                        fontSize: "16px",
                                                        color: "#475569",
                                                        fontWeight: "400",
                                                        caretColor: "blue",
                                                        outline: "none",
                                                    }}
                                                    containerStyle="flex justify-between"
                                                />
                                                {state.error && (
                                                    <p className="text-xs text-red-500">{state.error}</p>
                                                )}
                                            </div>

                                            <div>
                                                {state.resendDisabled ? (
                                                    <p className="text-xs text-gray-600">
                                                        Resend OTP in {state.resendCountdown} seconds
                                                    </p>
                                                ) : (
                                                    <button
                                                        onClick={handleResendOTPClick}
                                                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                                    >
                                                        Resend OTP
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Phone Verified Message */}
                            {state.phoneVerified && !state.showAdditionalInputs && (
                                <div className="space-y-2 p-3 bg-green-50 rounded-md">
                                    <p className="text-sm font-semibold text-gray-800">
                                        {formatPhoneNumber(state.phoneValue)}
                                    </p>
                                    <p className="text-xs text-green-600 font-medium">
                                        Phone Number Verified Successfully!
                                    </p>
                                </div>
                            )}

                            {/* Action Buttons */}
                            {!state.phoneVerified && (
                                <button
                                    type="submit"
                                    className={`w-full py-3 rounded-lg text-sm font-medium tracking-wider transition-colors ${state.otpSent && !state.isPhoneInputVisible
                                            ? state.otp.length === OTP_LENGTH
                                                ? "bg-BluntRed hover:bg-red-700 text-white"
                                                : "bg-gray-300 text-gray-600 cursor-not-allowed"
                                            : state.isValidNumber
                                                ? "bg-BluntRed hover:bg-red-700 text-white"
                                                : "bg-gray-300 text-gray-600 cursor-not-allowed"
                                        }`}
                                    onClick={
                                        state.otpSent && !state.isPhoneInputVisible
                                            ? handleVerifyOTPClick
                                            : handleSubmitForm
                                    }
                                    disabled={
                                        state.otpSent
                                            ? state.otp.length !== OTP_LENGTH
                                            : !state.isValidNumber
                                    }
                                >
                                    {state.otpSent && !state.isPhoneInputVisible
                                        ? "VERIFY OTP"
                                        : "SEND OTP"}
                                </button>
                            )}

                            {/* Additional User Details Form */}
                            {state.phoneVerified && state.showAdditionalInputs && (
                                <div className="space-y-4">
                                    <fieldset className="border border-gray-300 rounded-md p-3">
                                        <legend className="text-xs text-gray-600 px-1">Name *</legend>
                                        <div className="flex items-center space-x-2">
                                            <div className="relative w-4 h-4 flex-shrink-0">
                                                <ImageWrapper src="/icons/Profile_Red.svg" alt="Name icon" width={16} height={16} />
                                            </div>
                                            <input
                                                type="text"
                                                placeholder="Enter your full name"
                                                className="w-full border-0 bg-transparent focus:outline-none text-sm"
                                                value={state.formData.userName}
                                                onChange={(e) =>
                                                    dispatch({
                                                        type: "SET_FORM_DATA",
                                                        payload: { userName: e.target.value },
                                                    })
                                                }
                                            />
                                        </div>
                                    </fieldset>

                                    <fieldset className="border border-gray-300 rounded-md p-3">
                                        <legend className="text-xs text-gray-600 px-1">Gender *</legend>
                                        <select
                                            value={state.formData.gender}
                                            onChange={(e) =>
                                                dispatch({
                                                    type: "SET_FORM_DATA",
                                                    payload: { gender: e.target.value },
                                                })
                                            }
                                            className="w-full border-0 bg-transparent focus:outline-none text-sm text-gray-700"
                                        >
                                            <option value="" disabled>Select Gender</option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                            <option value="others">Others</option>
                                        </select>
                                    </fieldset>

                                    <fieldset className="border border-gray-300 rounded-md p-3">
                                        <legend className="text-xs text-gray-600 px-1">
                                            Date of Birth (Optional)
                                        </legend>
                                        <input
                                            type="date"
                                            className="w-full border-0 bg-transparent focus:outline-none text-sm"
                                            value={state.formData.dob}
                                            onChange={(e) =>
                                                dispatch({
                                                    type: "SET_FORM_DATA",
                                                    payload: { dob: e.target.value },
                                                })
                                            }
                                            max={new Date().toISOString().split('T')[0]}
                                        />
                                    </fieldset>

                                    <button
                                        type="button"
                                        onClick={handleFinishButtonClick}
                                        className="w-full bg-BluntRed hover:bg-red-700 text-white py-3 rounded-lg text-sm font-medium tracking-wider transition-colors"
                                    >
                                        FINISH
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer Links */}
                <p className="text-center text-xs text-gray-600 px-4">
                    <Link href="/terms-and-conditions" className="text-BluntRed hover:underline">
                        Terms and Conditions
                    </Link>
                    {" and "}
                    <Link href="/privacy-policy" className="text-BluntRed hover:underline">
                        Privacy Statement
                    </Link>
                    {" of BookMyPooja"}
                </p>

                {/* Pandit Account Button */}
                <div className="px-4">
                    <button
                        onClick={() => router.push("/auth/pandit")}
                        className="w-full py-3 text-sm text-BluntRed border border-BluntRed rounded-lg hover:bg-red-50 transition-colors"
                    >
                        Pandit Account
                    </button>
                </div>
            </div>
        </div>
        </>
    );
};